import React, { useEffect } from 'react';
import MessageBubble from './MessageBubble';

const SESSION_STATUS_LABELS = {
  active: 'Ativa',
  closed: 'Encerrada',
};

const CLOSED_REASON_LABELS = {
  timeout: 'Inatividade',
  escalation: 'Escalação',
  goal_completed: 'Objetivo concluído',
  context_switched: 'Mudança de contexto',
  manual: 'Fechamento manual',
};

function formatDateTime(iso) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function StatusBadge({ status }) {
  const styles = {
    active: 'text-emerald-700 bg-emerald-50',
    closed: 'text-slate-700 bg-slate-100',
  };

  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
        styles[status] || 'text-content-subtle bg-slate-100'
      }`}
    >
      {SESSION_STATUS_LABELS[status] || status}
    </span>
  );
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`h-4 w-4 text-content-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SessionMessageThread({ messagesState }) {
  if (messagesState.loading) {
    return (
      <p className="text-sm text-content-muted py-2 text-center">Carregando mensagens...</p>
    );
  }

  if (messagesState.error) {
    return (
      <p className="text-sm text-red-600 py-2 text-center">{messagesState.error}</p>
    );
  }

  if (!messagesState.data?.length) {
    return (
      <p className="text-sm text-content-subtle italic py-2 text-center">
        Nenhuma mensagem nesta sessão.
      </p>
    );
  }

  return (
    <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
      {messagesState.data.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
    </div>
  );
}

function hasSummaryText(summary) {
  return Boolean(summary && String(summary).trim());
}

export default function SessionCard({
  session,
  expanded,
  messagesState,
  onToggle,
  onLoadMessages,
  onCloseSession,
  closing = false,
}) {
  const isClosed = session.status === 'closed';
  const canClose = !isClosed && typeof onCloseSession === 'function';
  const summaryText = hasSummaryText(session.summary) ? session.summary.trim() : '';
  const closedReason = session.closed_reason
    ? CLOSED_REASON_LABELS[session.closed_reason] || session.closed_reason
    : null;

  const messageCount = messagesState?.data
    ? messagesState.meta?.total ?? messagesState.data.length
    : null;

  useEffect(() => {
    if (expanded && !messagesState?.data && !messagesState?.loading && !messagesState?.error) {
      onLoadMessages(session.id);
    }
  }, [expanded, messagesState, session.id, onLoadMessages]);

  const handleCloseClick = (event) => {
    event.stopPropagation();
    if (!canClose || closing) return;
    onCloseSession(session.id);
  };

  return (
    <li className="surface-card overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(session.id)}
        className="w-full px-4 py-4 text-left hover:bg-slate-50/60 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <StatusBadge status={session.status} />
              {closedReason && (
                <span className="text-xs text-content-muted">Motivo: {closedReason}</span>
              )}
              {messageCount !== null && (
                <span className="text-xs text-content-subtle">
                  {messageCount} mensagem{messageCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="text-xs text-content-muted space-y-1">
              {session.started_at && <p>Início: {formatDateTime(session.started_at)}</p>}
              {session.closed_at && <p>Fim: {formatDateTime(session.closed_at)}</p>}
            </div>

            {!expanded && isClosed && (
              summaryText ? (
                <p className="text-sm text-content-muted line-clamp-3 whitespace-pre-wrap">
                  {summaryText}
                </p>
              ) : (
                <p className="text-sm text-content-subtle italic">Sem resumo gerado</p>
              )
            )}

            {!expanded && !isClosed && summaryText && (
              <p className="text-sm text-content-muted line-clamp-3 whitespace-pre-wrap">
                {summaryText}
              </p>
            )}
          </div>

          <ChevronIcon expanded={expanded} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 px-4 py-4 space-y-4">
          {isClosed && (
            <div>
              <p className="text-xs font-medium text-content-primary mb-1">Resumo da sessão</p>
              {summaryText ? (
                <p className="text-sm text-content-muted whitespace-pre-wrap">{summaryText}</p>
              ) : (
                <p className="text-sm text-content-subtle italic">Sem resumo gerado</p>
              )}
            </div>
          )}

          {!isClosed && summaryText && (
            <div>
              <p className="text-xs font-medium text-content-primary mb-1">Resumo da sessão</p>
              <p className="text-sm text-content-muted whitespace-pre-wrap">{summaryText}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-content-primary mb-2">Mensagens</p>
            <SessionMessageThread messagesState={messagesState || {}} />
          </div>

          {canClose && (
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleCloseClick}
                disabled={closing}
                className="text-sm text-amber-700 hover:text-amber-800 disabled:opacity-40"
              >
                {closing ? 'Fechando...' : 'Fechar sessão'}
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
